/**
 * Extracted billing slip form — shared between BillingSlipModal (portrait FAB)
 * and ReviewLayout (inline landscape panel on AI Billing Review).
 *
 * This is the single source of truth for billing entry creation. Both the modal
 * and the inline panel render this component with different props.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Play, Square, ChevronDown } from 'lucide-react';
import { createBillingEntry, updateBillingEntry, getResolvedRate, type BillingEntryCreatePayload, type BillingEntryUpdatePayload } from '../api/billing';
import { getMatters, getMatter } from '../api/matters';

// ── Constants ─────────────────────────────────────────────

type EntryType = 'hourly' | 'flat_fee_trigger' | 'contingency_internal' | 'expense';
type BillableStatus = 'billable' | 'pro_bono' | 'non_billable';

const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  hourly: 'Hourly',
  flat_fee_trigger: 'Flat Fee',
  contingency_internal: 'Contingency',
  expense: 'Expense',
};

const ACTIVITY_CATEGORIES = [
  'Research', 'Drafting', 'Court Appearance', 'Client Communication',
  'Case Review', 'Document Review', 'Administrative', 'Travel', 'Other',
];

const EXPENSE_CATEGORY_LABELS: Record<string, string> = {
  filing_fee: 'Filing Fee',
  medical_records: 'Medical Records',
  postage: 'Postage',
  expert_witness: 'Expert Witness',
  deposition_transcript: 'Deposition Transcript',
  process_server: 'Process Server',
  travel: 'Travel',
  other: 'Other',
  ai_research: 'AI / Computerized Research',
};

// ── Milestone presets ─────────────────────────────────────

interface MilestonePreset { label: string; amountDollars: string; }
const PRESETS_KEY = 'billing_milestone_presets';

function loadMilestonePresets(): MilestonePreset[] {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || '[]'); } catch { return []; }
}

function persistMilestonePreset(existing: MilestonePreset[], preset: MilestonePreset): MilestonePreset[] {
  const updated = [
    ...existing.filter(p => p.label.toLowerCase() !== preset.label.toLowerCase()),
    preset,
  ];
  localStorage.setItem(PRESETS_KEY, JSON.stringify(updated));
  return updated;
}

// ── Helpers ───────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function roundUpToIncrement(minutes: number, increment: number): number {
  if (minutes <= 0) return 0;
  if (increment <= 1) return minutes;
  return Math.ceil(minutes / increment) * increment;
}

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// ── Field styles ──────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem 0.75rem',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8rem', fontWeight: 600,
  color: 'var(--text-secondary)', marginBottom: '0.35rem',
};

// ── Props ─────────────────────────────────────────────────

export interface BillingSlipFormProps {
  /** Called after successful save. Modal uses this to close; inline uses it to reset. */
  onSaved: () => void;
  /** Called when user clicks Cancel/Clear. */
  onCancel?: () => void;
  /** Pre-select matter (inline review passes the active bucket's matter id). */
  defaultMatterId?: number;
  /** When true, the matter picker is hidden and matterId is locked. */
  lockMatter?: boolean;
  // Pre-population from AI suggestion
  defaultEntryType?: string;
  defaultDescription?: string;
  defaultDurationMinutes?: number;
  defaultAmountCents?: number;
  defaultRateCents?: number;
  defaultBillingDate?: string;
  autoStartTimer?: boolean;
  stopTimerSignal?: number;
  onTimerSnapshot?: (snapshot: { state: 'idle' | 'running' | 'stopped'; elapsedSeconds: number }) => void;
  /** Phase 6: when set, the suggestion is marked accepted as part of slip save. */
  acceptedSuggestionId?: number;
  /** When set, the form is in edit mode — updates an existing entry instead of creating. */
  editEntryId?: number;
  /** Label for the save button (default "Save Entry"). */
  saveLabel?: string;
  /** Label for the cancel button (default "Cancel"). */
  cancelLabel?: string;
  /** Show the Hide button (modal only). */
  onHide?: () => void;
}

// ── Component ─────────────────────────────────────────────

export default function BillingSlipForm({
  onSaved, onCancel, onHide,
  defaultMatterId, lockMatter = false,
  defaultEntryType, defaultDescription, defaultDurationMinutes,
  defaultAmountCents, defaultRateCents, defaultBillingDate,
  autoStartTimer = false,
  stopTimerSignal = 0,
  onTimerSnapshot,
  acceptedSuggestionId,
  editEntryId,
  saveLabel,
  cancelLabel = 'Cancel',
}: BillingSlipFormProps) {
  const isEditMode = editEntryId !== undefined;
  const effectiveSaveLabel = saveLabel ?? (isEditMode ? 'Update Entry' : 'Save Entry');
  const queryClient = useQueryClient();

  // Entry type
  const [entryType, setEntryType] = useState<EntryType>((defaultEntryType as EntryType) ?? 'hourly');

  // Matter picker
  const [matterSearch, setMatterSearch] = useState('');
  const debouncedSearch = useDebounce(matterSearch, 300);
  const [showDropdown, setShowDropdown] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const milestoneBlurRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [matterId, setMatterId] = useState<number | null>(defaultMatterId ?? null);
  const [selectedMatterInfo, setSelectedMatterInfo] = useState<{ id: number; title: string } | null>(null);

  // Common fields
  const [description, setDescription] = useState(defaultDescription ?? '');
  const [billingDate, setBillingDate] = useState(defaultBillingDate ?? todayStr());
  const [billableStatus, setBillableStatus] = useState<BillableStatus>('billable');
  const [activityCategory, setActivityCategory] = useState('');

  // Milestone presets (flat fee)
  const [milestonePresets, setMilestonePresets] = useState<MilestonePreset[]>(loadMilestonePresets);
  const [showMilestoneDropdown, setShowMilestoneDropdown] = useState(false);

  // Hourly / Contingency
  const [durationMinutes, setDurationMinutes] = useState<string>(
    defaultDurationMinutes ? String(defaultDurationMinutes) : ''
  );
  // Rate override — user can always edit; defaults from resolved rate, prop, or $100/hr
  const [rateOverride, setRateOverride] = useState<string>(
    defaultRateCents ? (defaultRateCents / 100).toFixed(2) : ''
  );
  const [rateInitialized, setRateInitialized] = useState(!!defaultRateCents);
  const [timerState, setTimerState] = useState<'idle' | 'running' | 'stopped'>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerStartRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStartedRef = useRef(false);

  // Flat fee
  const [phaseMilestone, setPhaseMilestone] = useState('');

  // Expense
  const [amountDollars, setAmountDollars] = useState(
    defaultAmountCents !== undefined ? (defaultAmountCents / 100).toFixed(2) : ''
  );
  const [expenseCategory, setExpenseCategory] = useState('');
  const [vendor, setVendor] = useState('');
  const [receiptReference, setReceiptReference] = useState('');

  // Matter queries
  const { data: mattersData } = useQuery({
    queryKey: ['billing-matters', { search: debouncedSearch, limit: 20 }],
    queryFn: () => getMatters({ search: debouncedSearch || undefined, limit: 20 }),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30000,
  });

  const matters = debouncedSearch.length >= 2 ? (mattersData?.items ?? []) : [];

  const { data: initialMatter } = useQuery({
    queryKey: ['billing-matter-lookup', matterId],
    queryFn: () => getMatter(matterId!),
    enabled: matterId !== null && selectedMatterInfo === null,
    staleTime: Infinity,
  });

  const selectedMatter =
    selectedMatterInfo?.id === matterId
      ? selectedMatterInfo
      : initialMatter && initialMatter.id === matterId
        ? { id: initialMatter.id, title: initialMatter.title }
        : null;

  // Rate resolution
  const { data: resolvedRate } = useQuery({
    queryKey: ['billing-rate', matterId],
    queryFn: () => getResolvedRate(matterId ?? undefined),
    enabled: matterId !== null,
    staleTime: 60_000,
  });
  const roundingMinutes = resolvedRate?.rounding_minutes ?? 6;

  // Prefill rate from resolved rate once on first load (not in effect — avoids lint error)
  if (!rateInitialized && resolvedRate?.rate_cents && rateOverride === '') {
    setRateOverride((resolvedRate.rate_cents / 100).toFixed(2));
    setRateInitialized(true);
  }

  // Timer
  const startTimer = useCallback(() => {
    timerStartRef.current = Date.now();
    setTimerState('running');
    timerIntervalRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - timerStartRef.current!) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    const mins = Math.ceil(elapsedSeconds / 60);
    setTimerState('stopped');
    setDurationMinutes(String(mins > 0 ? mins : 1));
  }, [elapsedSeconds]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    onTimerSnapshot?.({ state: timerState, elapsedSeconds });
  }, [elapsedSeconds, onTimerSnapshot, timerState]);

  useEffect(() => {
    if (stopTimerSignal > 0 && timerState === 'running') {
      const timeoutId = window.setTimeout(stopTimer, 0);
      return () => window.clearTimeout(timeoutId);
    }
    return undefined;
  }, [stopTimer, stopTimerSignal, timerState]);

  // Reset timer when switching entry types
  function selectEntryType(t: EntryType) {
    if (timerState === 'running') stopTimer();
    setEntryType(t);
    setDurationMinutes('');
    setTimerState('idle');
    setElapsedSeconds(0);
    if (t === 'contingency_internal') setBillableStatus('non_billable');
  }

  // Mutations
  const invalidateOnSuccess = (matterId: number) => {
    queryClient.invalidateQueries({ queryKey: ['billing-entries'] });
    queryClient.invalidateQueries({ queryKey: ['billing-entries-review'] });
    queryClient.invalidateQueries({ queryKey: ['billing-entries-list'] });
    queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    queryClient.invalidateQueries({ queryKey: ['today-entries'] });
    queryClient.invalidateQueries({ queryKey: ['running-timer'] });
    queryClient.invalidateQueries({ queryKey: ['matter-time-entries', matterId] });
    queryClient.invalidateQueries({ queryKey: ['expenses', matterId] });
    queryClient.invalidateQueries({ queryKey: ['matter-economics', matterId] });
    queryClient.invalidateQueries({ queryKey: ['activities', matterId] });
    if (acceptedSuggestionId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ['billing-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['billing-pending-count'] });
    }
  };

  const createMutation = useMutation({
    mutationFn: createBillingEntry,
    onSuccess: (entry) => { invalidateOnSuccess(entry.matter_id); onSaved(); },
  });

  const editMutation = useMutation({
    mutationFn: (data: BillingEntryUpdatePayload) => updateBillingEntry(editEntryId!, data),
    onSuccess: (entry) => { invalidateOnSuccess(entry.matter_id); onSaved(); },
  });

  const saveMutation = isEditMode ? editMutation : createMutation;

  // Validation
  const narrativeOk = description.trim().length >= 10;
  const matterOk = typeof matterId === 'number' && Number.isInteger(matterId) && matterId > 0;
  const isTimedType = entryType === 'hourly' || entryType === 'contingency_internal';
  const isTimerCreatedEntry = isTimedType && timerState === 'stopped';

  const typeFieldsOk = (() => {
    if (entryType === 'hourly' || entryType === 'contingency_internal') {
      return (durationMinutes !== '' && Number(durationMinutes) > 0) || timerState === 'stopped';
    }
    if (entryType === 'expense') {
      return amountDollars !== '' && Number(amountDollars) > 0 && expenseCategory !== '';
    }
    if (entryType === 'flat_fee_trigger') {
      return amountDollars !== '' && Number(amountDollars) > 0;
    }
    return true;
  })();

  const canSave = matterOk && narrativeOk && typeFieldsOk && !saveMutation.isPending;

  function handleSave() {
    if (!canSave || !matterOk || matterId === null) return;

    const durationNum = durationMinutes !== '' ? Number(durationMinutes) : undefined;
    const amountCents = amountDollars !== '' ? Math.round(Number(amountDollars) * 100) : undefined;

    const effectiveRateCents = rateOverride ? Math.round(Number(rateOverride) * 100) : (resolvedRate?.rate_cents ?? 10000);

    if (isEditMode) {
      const updatePayload: BillingEntryUpdatePayload = {
        entry_type: entryType,
        billing_date: billingDate,
        description: description.trim(),
        billable: billableStatus === 'billable',
        is_pro_bono: billableStatus === 'pro_bono',
        activity_category: activityCategory || undefined,
        duration_minutes: entryType !== 'expense' && durationNum ? durationNum : undefined,
        rate_cents: entryType === 'hourly' ? effectiveRateCents : undefined,
        phase_milestone: entryType === 'flat_fee_trigger' ? phaseMilestone || undefined : undefined,
        amount_cents: (entryType === 'expense' || entryType === 'flat_fee_trigger') ? amountCents : undefined,
        expense_category: entryType === 'expense' ? expenseCategory || undefined : undefined,
        vendor: entryType === 'expense' ? vendor || undefined : undefined,
        receipt_reference: entryType === 'expense' ? receiptReference || undefined : undefined,
      };
      editMutation.mutate(updatePayload);
    } else {
      const createPayload: BillingEntryCreatePayload = {
        matter_id: matterId,
        entry_type: entryType,
        source: isTimerCreatedEntry ? 'timer' : 'manual',
        billing_date: billingDate,
        description: description.trim(),
        billable: billableStatus === 'billable',
        is_pro_bono: billableStatus === 'pro_bono',
        activity_category: activityCategory || undefined,
        duration_minutes: entryType !== 'expense' && durationNum ? durationNum : undefined,
        rate_cents: entryType === 'hourly' ? effectiveRateCents : undefined,
        phase_milestone: entryType === 'flat_fee_trigger' ? phaseMilestone || undefined : undefined,
        amount_cents: (entryType === 'expense' || entryType === 'flat_fee_trigger') ? amountCents : undefined,
        expense_category: entryType === 'expense' ? expenseCategory || undefined : undefined,
        vendor: entryType === 'expense' ? vendor || undefined : undefined,
        receipt_reference: entryType === 'expense' ? receiptReference || undefined : undefined,
        accepted_suggestion_id: acceptedSuggestionId,
      };
      createMutation.mutate(createPayload);
    }
  }

  useEffect(() => {
    if (!autoStartTimer || autoStartedRef.current || !isTimedType || timerState !== 'idle') return;
    const timeoutId = window.setTimeout(() => {
      if (autoStartedRef.current) return;
      autoStartedRef.current = true;
      startTimer();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [autoStartTimer, isTimedType, startTimer, timerState]);

  // Milestone combobox derived values (flat fee)
  const filteredPresets = milestonePresets.filter(p =>
    phaseMilestone.length === 0 || p.label.toLowerCase().includes(phaseMilestone.toLowerCase())
  );
  const presetMatch = milestonePresets.find(
    p => p.label.toLowerCase() === phaseMilestone.trim().toLowerCase()
  );
  const canSaveAsPreset =
    phaseMilestone.trim().length > 0 &&
    amountDollars !== '' &&
    Number(amountDollars) > 0 &&
    presetMatch?.amountDollars !== amountDollars;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Entry type segmented control */}
      <div>
        <label style={labelStyle}>Entry Type</label>
        <div style={{
          display: 'flex', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', overflow: 'hidden',
        }}>
          {(Object.entries(ENTRY_TYPE_LABELS) as [EntryType, string][]).map(([type, label]) => (
            <button
              key={type}
              type="button"
              onClick={() => selectEntryType(type)}
              style={{
                flex: 1, padding: '0.45rem 0.25rem',
                border: 'none', borderRight: '1px solid var(--border)',
                background: entryType === type ? 'var(--primary)' : 'var(--card)',
                color: entryType === type ? '#fff' : 'var(--text)',
                cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                transition: 'background 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Matter picker — hidden when locked (inline review already shows matter in tabs) */}
      {!lockMatter && (
        <div>
          <label style={labelStyle}>Matter *</label>
          {selectedMatter ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.5rem 0.75rem', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', background: 'var(--bg)', fontSize: '0.85rem',
            }}>
              <span style={{ fontWeight: 500 }}>{selectedMatter.title}</span>
              <button
                type="button"
                onClick={() => { setMatterId(null); setSelectedMatterInfo(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '0.78rem', fontWeight: 600 }}
              >
                Change
              </button>
            </div>
          ) : (
            <div>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  value={matterSearch}
                  onChange={(e) => { setMatterSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => { clearTimeout(blurTimeoutRef.current); setShowDropdown(true); }}
                  onBlur={() => { blurTimeoutRef.current = setTimeout(() => setShowDropdown(false), 200); }}
                  placeholder="Search matters... (type 2+ characters)"
                  style={{
                    ...inputStyle,
                    paddingLeft: '2rem',
                    borderRadius: showDropdown && matters.length > 0
                      ? 'var(--radius-sm) var(--radius-sm) 0 0'
                      : 'var(--radius-sm)',
                  }}
                />
              </div>
              {showDropdown && matters.length > 0 && (
                <div style={{
                  border: '1px solid var(--border)', borderTop: 'none',
                  borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                  maxHeight: 180, overflowY: 'auto',
                }}>
                  {matters.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setMatterId(m.id);
                        setSelectedMatterInfo({ id: m.id, title: m.title });
                        setMatterSearch('');
                        setShowDropdown(false);
                      }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '0.45rem 0.75rem', border: 'none', background: 'var(--card)',
                        cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text)',
                        borderBottom: '1px solid var(--border-light)',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card)'}
                    >
                      <div style={{ fontWeight: 500 }}>{m.title}</div>
                      {m.contact_name && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{m.contact_name}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && matterSearch.length >= 2 && debouncedSearch !== matterSearch && (
                <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Searching...
                </div>
              )}
              {!matterOk && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: 'var(--danger)' }}>
                  Choose a matter before saving this billing slip.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duration / Timer — hourly + contingency */}
      {isTimedType && (
        <div>
          <label style={labelStyle}>Duration *</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {timerState !== 'idle' && (
              <div style={{
                fontVariantNumeric: 'tabular-nums', fontSize: '1.1rem', fontWeight: 700,
                color: timerState === 'running' ? '#16a34a' : 'var(--text)',
                minWidth: 72,
              }}>
                {formatElapsed(elapsedSeconds)}
              </div>
            )}
            {timerState === 'idle' && (
              <button
                type="button"
                onClick={startTimer}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.45rem 0.75rem', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', background: 'var(--card)',
                  color: '#16a34a', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                }}
              >
                <Play size={13} /> Start Timer
              </button>
            )}
            {timerState === 'running' && (
              <button
                type="button"
                onClick={stopTimer}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.45rem 0.75rem', border: '1px solid #ef4444',
                  borderRadius: 'var(--radius-sm)', background: 'var(--card)',
                  color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                }}
              >
                <Square size={13} /> Stop
              </button>
            )}
            {timerState === 'stopped' && (
              <button
                type="button"
                onClick={() => { setTimerState('idle'); setElapsedSeconds(0); setDurationMinutes(''); }}
                style={{
                  padding: '0.45rem 0.75rem', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', background: 'var(--card)',
                  color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.78rem',
                }}
              >
                Reset
              </button>
            )}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input
                type="number"
                min={1}
                step={1}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                placeholder={timerState === 'running' ? 'Timer running...' : 'Minutes'}
                disabled={timerState === 'running'}
                style={{ ...inputStyle, opacity: timerState === 'running' ? 0.4 : 1 }}
              />
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>min</span>
            </div>
          </div>
          {/* Rate input — hourly only */}
          {entryType === 'hourly' && (
            <div style={{ marginTop: '0.5rem' }}>
              <label style={labelStyle}>Rate ($/hr)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={rateOverride}
                onChange={(e) => setRateOverride(e.target.value)}
                placeholder="100.00"
                style={{ ...inputStyle, maxWidth: 160 }}
              />
            </div>
          )}
          {entryType === 'hourly' && (() => {
            const enteredMins = durationMinutes !== '' ? Number(durationMinutes) : 0;
            if (!enteredMins || enteredMins <= 0) return null;
            const billedMins = roundUpToIncrement(enteredMins, roundingMinutes);
            const effectiveRateCents = rateOverride ? Math.round(Number(rateOverride) * 100) : (resolvedRate?.rate_cents ?? 10000);
            const amountCents = Math.round((billedMins / 60) * effectiveRateCents);
            const wasRounded = billedMins !== enteredMins;
            return (
              <div style={{
                marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                background: 'var(--bg)', borderRadius: 'var(--radius-sm)',
                fontSize: '0.78rem', color: 'var(--text-secondary)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem',
              }}>
                <span>
                  {wasRounded ? (
                    <>Bills as <strong>{billedMins} min</strong> ({(billedMins / 60).toFixed(2)} hr) — rounded to {roundingMinutes}-min increments</>
                  ) : (
                    <>{(billedMins / 60).toFixed(2)} hr</>
                  )}
                </span>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                  {formatDollars(amountCents)} @ {formatDollars(effectiveRateCents)}/hr
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Phase milestone + amount — flat fee */}
      {entryType === 'flat_fee_trigger' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Phase / Milestone</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={phaseMilestone}
                  onChange={(e) => { setPhaseMilestone(e.target.value); setShowMilestoneDropdown(true); }}
                  onFocus={() => { clearTimeout(milestoneBlurRef.current); setShowMilestoneDropdown(true); }}
                  onBlur={() => { milestoneBlurRef.current = setTimeout(() => setShowMilestoneDropdown(false), 200); }}
                  placeholder="Select or type a milestone..."
                  style={{
                    ...inputStyle,
                    paddingRight: '2rem',
                    borderRadius: showMilestoneDropdown
                      ? 'var(--radius-sm) var(--radius-sm) 0 0'
                      : 'var(--radius-sm)',
                  }}
                />
                <ChevronDown
                  size={14}
                  style={{
                    position: 'absolute', right: 8, top: '50%',
                    transform: `translateY(-50%) rotate(${showMilestoneDropdown ? '180deg' : '0deg'})`,
                    color: 'var(--text-muted)', pointerEvents: 'none',
                    transition: 'transform 0.15s',
                  }}
                />
                {showMilestoneDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    border: '1px solid var(--border)', borderTop: 'none',
                    borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                    background: 'var(--card)', zIndex: 10,
                    maxHeight: 160, overflowY: 'auto',
                  }}>
                    {filteredPresets.length > 0 ? filteredPresets.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setPhaseMilestone(p.label);
                          setAmountDollars(p.amountDollars);
                          setShowMilestoneDropdown(false);
                        }}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          width: '100%', padding: '0.45rem 0.75rem',
                          border: 'none', borderBottom: '1px solid var(--border-light)',
                          background: 'var(--card)', cursor: 'pointer',
                          fontSize: '0.82rem', color: 'var(--text)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--card)'; }}
                      >
                        <span style={{ fontWeight: 500 }}>{p.label}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>${p.amountDollars}</span>
                      </button>
                    )) : (
                      <div style={{
                        padding: '0.5rem 0.75rem', fontSize: '0.78rem',
                        color: 'var(--text-muted)', fontStyle: 'italic',
                      }}>
                        No presets yet — type a name, enter an amount, then save as preset
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div style={{ flex: '0 0 140px' }}>
              <label style={labelStyle}>Amount ($) *</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                placeholder="0.00"
                style={inputStyle}
              />
            </div>
          </div>
          {canSaveAsPreset && (
            <button
              type="button"
              onClick={() => {
                const updated = persistMilestonePreset(milestonePresets, {
                  label: phaseMilestone.trim(),
                  amountDollars,
                });
                setMilestonePresets(updated);
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--primary)', fontSize: '0.75rem',
                padding: '0.1rem 0', textDecoration: 'underline', alignSelf: 'flex-start',
              }}
            >
              Save &quot;{phaseMilestone.trim()}&quot; as preset
            </button>
          )}
        </>
      )}

      {/* Expense fields */}
      {entryType === 'expense' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Category *</label>
              <select
                value={expenseCategory}
                onChange={(e) => setExpenseCategory(e.target.value)}
                style={inputStyle}
              >
                <option value="">-- Select --</option>
                {Object.entries(EXPENSE_CATEGORY_LABELS).map(([val, lbl]) => (
                  <option key={val} value={val}>{lbl}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Amount ($) *</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                placeholder="0.00"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Vendor</label>
              <input
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="Vendor name"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Receipt / Reference</label>
              <input
                type="text"
                value={receiptReference}
                onChange={(e) => setReceiptReference(e.target.value)}
                placeholder="Receipt #, check #, etc."
                style={inputStyle}
              />
            </div>
          </div>
        </>
      )}

      {/* Narrative */}
      <div>
        <label style={labelStyle}>
          Narrative *
          {description.trim().length > 0 && description.trim().length < 10 && (
            <span style={{ color: '#ef4444', fontWeight: 400, marginLeft: '0.4rem' }}>
              ({10 - description.trim().length} more characters needed)
            </span>
          )}
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the work performed..."
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', minHeight: 72 }}
        />
      </div>

      {/* Activity category + date (not for expense) */}
      {entryType !== 'expense' && (
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Activity Category</label>
            <select value={activityCategory} onChange={(e) => setActivityCategory(e.target.value)} style={inputStyle}>
              <option value="">-- None --</option>
              {ACTIVITY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date</label>
            <input
              type="date"
              value={billingDate}
              onChange={(e) => setBillingDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
      )}

      {/* Date for expense */}
      {entryType === 'expense' && (
        <div>
          <label style={labelStyle}>Date</label>
          <input
            type="date"
            value={billingDate}
            onChange={(e) => setBillingDate(e.target.value)}
            style={inputStyle}
          />
        </div>
      )}

      {/* Billing status */}
      <div>
        <label style={labelStyle}>Billing Status</label>
        <div style={{
          display: 'flex', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', overflow: 'hidden',
        }}>
          {([
            ['billable', 'Billable'],
            ['pro_bono', 'Pro Bono'],
            ['non_billable', 'Non-Billable'],
          ] as [BillableStatus, string][]).map(([status, label], i) => (
            <button
              key={status}
              type="button"
              onClick={() => setBillableStatus(status)}
              style={{
                flex: 1, padding: '0.4rem 0.25rem',
                border: 'none',
                borderRight: i < 2 ? '1px solid var(--border)' : 'none',
                background: billableStatus === status ? 'var(--primary)' : 'var(--card)',
                color: billableStatus === status ? '#fff' : 'var(--text)',
                cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                transition: 'background 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {saveMutation.isError && (
        <div style={{ fontSize: '0.82rem', color: '#ef4444', padding: '0.5rem 0.75rem', background: '#fef2f2', borderRadius: 'var(--radius-sm)' }}>
          {(saveMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save entry — please try again.'}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.25rem' }}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', background: 'var(--card)',
              color: 'var(--text)', cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            {cancelLabel}
          </button>
        )}
        {onHide && (
          <button
            type="button"
            onClick={onHide}
            title="Hide this slip — timer and data will be preserved"
            style={{
              padding: '0.5rem 1rem', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', background: 'var(--card)',
              color: 'var(--primary)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
            }}
          >
            Hide
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          title={!matterOk ? 'Choose a matter before saving this billing slip' : !narrativeOk ? 'Narrative must be at least 10 characters' : ''}
          style={{
            padding: '0.5rem 1.25rem', border: 'none',
            borderRadius: 'var(--radius-sm)',
            background: canSave ? 'var(--primary)' : 'var(--border)',
            color: canSave ? '#fff' : 'var(--text-muted)',
            cursor: canSave ? 'pointer' : 'not-allowed',
            fontSize: '0.85rem', fontWeight: 700,
            transition: 'background 0.15s',
          }}
        >
          {saveMutation.isPending ? 'Saving...' : effectiveSaveLabel}
        </button>
      </div>
    </div>
  );
}
